// client/src/components/ClientFid.jsx

import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import ClientNavbar from './ClientNavbar';
import { FaArrowLeft } from 'react-icons/fa';
import {
  getAuth,
  EmailAuthProvider,
  linkWithCredential,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  limit as fsLimit,
  onSnapshot,
} from 'firebase/firestore';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { firestore, storage } from '../../firebase';
import { REGEX as PASSWORD_REGEX, POLICY as PASSWORD_POLICY } from '../../policies/passwordPolicy';
import PasswordField from '../shared/PasswordField';
import LegalAccordion from '../shared/LegalAccordion';
import { AuthContext } from '../../AuthContext';

/* ============================
   Config & cache
============================ */
const LOGO_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours
const MODAL_COOLDOWN_MS = 30 * 60 * 1000;
const MODAL_KEY = 'createAccountModalClosedAt';
const LEGACY_SCAN_LIMIT = 30; // scan borné si cache vide
const CARD_CACHE_PREFIX = 'clientfid_cards_';
const CARD_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

/* ============================
   Helpers (dates & tri)
============================ */
const toMs = (v) => {
  if (v && typeof v === 'object') {
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (v.seconds !== undefined && v.nanoseconds !== undefined) {
      return v.seconds * 1000 + Math.floor(v.nanoseconds / 1e6);
    }
  }
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    if (!Number.isNaN(t)) return t;
  }
  return 0;
};

const computeLastActivityMs = (clientData) => {
  const candidates = [
    clientData?.lastScanAt,
    clientData?.lastPassAt,
    clientData?.lastVisitAt,
    clientData?.lastVisit,
    clientData?.updatedAt,
    clientData?.lastUpdatedAt,
    clientData?.date,
    clientData?.ts,
    clientData?.timestamp,
    clientData?.createdAt,
  ];
  let max = 0;
  for (const c of candidates) {
    const m = toMs(c);
    if (m > max) max = m;
  }
  return max;
};

const uniqSorted = (arr = []) =>
  Array.from(new Set((Array.isArray(arr) ? arr : []).filter(Boolean))).sort();

const hasActiveGain = (card) => {
  if (!card) return false;
  if (card.Type === 'Fidbycount') {
    return (card.multiple || 0) - (card.compteur || 0) === 1;
  }
  if (card.Type === 'Fidbypoints') {
    const fr = card.firstRewardPoints || 0;
    return fr > 0 && (card.points || 0) >= fr;
  }
  return false;
};

const sortCards = (arr) => {
  return [...arr].sort((a, b) => {
    const ga = hasActiveGain(a);
    const gb = hasActiveGain(b);
    if (ga !== gb) return gb - ga; // gains actifs d'abord
    const ta = a.lastActivityMs || 0;
    const tb = b.lastActivityMs || 0;
    if (tb !== ta) return tb - ta; // plus récent d'abord
    const na = (a.merchantPseudo || '').toString();
    const nb = (b.merchantPseudo || '').toString();
    return na.localeCompare(nb);
  });
};

/* ============================
   MerchantLogo (cache localStorage + TTL)
============================ */
const MerchantLogo = ({ merchantId }) => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!merchantId) return;
    const cacheKey = `merchantLogo_${merchantId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { url, ts } = JSON.parse(cached);
        if (url) setLogoUrl(url);
        if (ts && Date.now() - ts < LOGO_TTL_MS) return; // cache frais
      } catch {}
    }

    let mounted = true;
    (async () => {
      try {
        const profileRef = ref(storage, `${merchantId}/photo-profil/`);
        const res = await listAll(profileRef);
        if (res.items.length === 0) {
          localStorage.removeItem(cacheKey);
          if (mounted) setLogoUrl(null);
          return;
        }
        const withMeta = await Promise.all(
          res.items.map(async (item) => {
            const meta = await getMetadata(item);
            const t = meta.timeCreated ? new Date(meta.timeCreated).getTime() : 0;
            return { item, t };
          })
        );
        withMeta.sort((a, b) => b.t - a.t);
        const latestUrl = await getDownloadURL(withMeta[0].item);
        localStorage.setItem(cacheKey, JSON.stringify({ url: latestUrl, ts: Date.now() }));
        if (mounted) setLogoUrl(latestUrl);
      } catch (e) {}
    })();

    return () => { mounted = false; };
  }, [merchantId]);

  return logoUrl ? (
    <img
      className="fid-card-logo"
      src={logoUrl}
      alt="Logo du commerçant"
      style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover', borderRadius: '50%' }}
      loading="lazy"
    />
  ) : null;
};

/* ============================
   Component
============================ */
const ClientFid = () => {
  const navigate = useNavigate();
  const { reloadEmailVerified } = useContext(AuthContext);

  const [fidelityData, setFidelityData] = useState([]);
  const [authLoaded, setAuthLoaded] = useState(false);

  // ✅ Hover actif uniquement sur desktop (≥769px) ET pointeur fin
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mqHoverFine = window.matchMedia('(hover: hover) and (pointer: fine)');
    const mqDesktop = window.matchMedia('(min-width: 769px)');

    const evaluate = () => setCanHover(Boolean(mqHoverFine.matches && mqDesktop.matches));

    evaluate();
    const add = (m, fn) => (m.addEventListener ? m.addEventListener('change', fn) : m.addListener(fn));
    const remove = (m, fn) => (m.removeEventListener ? m.removeEventListener('change', fn) : m.removeListener(fn));

    add(mqHoverFine, evaluate);
    add(mqDesktop, evaluate);
    window.addEventListener('orientationchange', evaluate);

    return () => {
      remove(mqHoverFine, evaluate);
      remove(mqDesktop, evaluate);
      window.removeEventListener('orientationchange', evaluate);
    };
  }, []);

  // caches & listeners
  const dataMap = useRef({});
  const merchantMetaCache = useRef({});
  const pendingMetaFetch = useRef(new Set());
  const perMerchantLegacyUnsub = useRef({});
  const perMerchantNewUnsub = useRef({});
  const attachedMerchants = useRef(new Set());
  const currentUidRef = useRef(null);

  // Création compte
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isPwdValid, setIsPwdValid] = useState(true);
  const [newPseudo, setNewPseudo] = useState('');
  const [acceptedCGU, setAcceptedCGU] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);

  const isCreateDisabled =
    creating || !newEmail.trim() || !newPassword.trim() || !newPseudo.trim() || !isPwdValid || !acceptedCGU;

  const handlePwdChange = (e) => {
    const v = e.target.value;
    setNewPassword(v);
    setIsPwdValid(PASSWORD_REGEX.test(v));
  };

  useEffect(() => setAuthLoaded(true), []);

  useEffect(() => {
    if (!authLoaded) return;
    const user = getAuth().currentUser;
    if (user?.isAnonymous) {
      const raw = localStorage.getItem(MODAL_KEY);
      const closed = Number.isFinite(parseInt(raw, 10)) ? parseInt(raw, 10) : 0;
      if (Date.now() - closed > MODAL_COOLDOWN_MS) setShowModal(true);
    }
  }, [authLoaded]);

  const handleCloseModal = () => {
    setShowModal(false);
    localStorage.setItem(MODAL_KEY, Date.now().toString());
  };

  const handleCreateAccount = async () => {
    if (isCreateDisabled) return;
    setCreating(true);
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user?.isAnonymous) { setCreating(false); return; }

    if (!PASSWORD_REGEX.test(newPassword)) {
      alert(PASSWORD_POLICY.message);
      setCreating(false);
      return;
    }

    try {
      const cred = EmailAuthProvider.credential(newEmail, newPassword);
      const linked = await linkWithCredential(user, cred);
      await setDoc(
        doc(firestore, 'users', linked.user.uid),
        { email: newEmail, pseudo: newPseudo, CGV: 'accepted', createdAt: serverTimestamp(), role: 'client' },
        { merge: true }
      );

      // Poser needsEmailVerification: true + déclencher la gate de vérification email
      try {
        const idToken = await linked.user.getIdToken();
        const apiBase = ((process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')) ||
          (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5099');
        await fetch(`${apiBase}/api/auth/flag-verification`, {
          method: 'POST',
          credentials: 'include',
          headers: { Authorization: `Bearer ${idToken}` },
        });
        // Force le rafraîchissement du token pour que AuthContext voie needsEmailVerification: true
        await reloadEmailVerified(true);
      } catch (flagErr) {
        console.warn('flag-verification non critique :', flagErr);
      }

      handleCloseModal();
    } catch (err) {
      console.error('Erreur création compte :', err);
      if (err.code === 'auth/email-already-in-use') alert('Email déjà utilisé.');
      else alert(`Erreur : ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const commitCards = useCallback(() => {
    const snapshot = Object.values(dataMap.current);
    const sorted = sortCards(snapshot);
    setFidelityData(sorted);

    const uid = currentUidRef.current;
    if (!uid) return;
    try {
      localStorage.setItem(
        `${CARD_CACHE_PREFIX}${uid}`,
        JSON.stringify({ ts: Date.now(), data: sorted })
      );
    } catch {}
  }, []);

  const upsertCard = useCallback(async (merchantId, clientData) => {
    if (!merchantMetaCache.current[merchantId] && !pendingMetaFetch.current.has(merchantId)) {
      pendingMetaFetch.current.add(merchantId);
      const base = { Type: 'Fidbycount', multiple: 0, gift: '', points1: 0, pseudo: 'Inconnu' };
      const pickPseudo = (d = {}) =>
        d.pseudo || d.entreprise_name || d.companyName || d.displayName || d.name || null;
      try {
        // 1) Doc fidélité (porte les réglages Type / multiple / points / gift)
        const fidSnap = await getDoc(doc(firestore, 'fidelity', merchantId));
        const fidData = fidSnap.exists() ? (fidSnap.data() || {}) : {};

        const metaFromFid = {
          Type: fidData.Type || fidData.type || base.Type,
          multiple: Number(fidData.multiple || 0) || base.multiple,
          gift: fidData.gift || base.gift,
          points1: Number(fidData.points1 || fidData.firstRewardPoints || 0) || base.points1,
          pseudo: fidData.pseudo || base.pseudo,
        };

        // 2) Compléter le pseudo via entreprises si besoin
        let pseudo = metaFromFid.pseudo;
        if (!pseudo || pseudo === 'Inconnu') {
          const entSnap = await getDoc(doc(firestore, 'entreprises', merchantId));
          if (entSnap.exists()) {
            pseudo = pickPseudo(entSnap.data() || {}) || pseudo;
          } else {
            const qs = await getDocs(
              query(collection(firestore, 'entreprises'), where('userId', '==', merchantId), fsLimit(1))
            );
            if (!qs.empty) {
              pseudo = pickPseudo(qs.docs[0].data() || {}) || pseudo;
            }
          }
        }

        merchantMetaCache.current[merchantId] = { ...base, ...metaFromFid, pseudo: pseudo || base.pseudo };
      } catch {
        merchantMetaCache.current[merchantId] = base;
      } finally {
        pendingMetaFetch.current.delete(merchantId);
      }
    }

    const meta = merchantMetaCache.current[merchantId] || {};
    const lastActivityMs = computeLastActivityMs(clientData || {});
    dataMap.current[merchantId] = {
      merchantId,
      merchantPseudo: meta.pseudo || 'Inconnu',
      multiple: meta.multiple || 0,
      compteur: clientData?.compteur || 0,
      gift: meta.gift || '',
      code: clientData?.code,
      Type: meta.Type || 'Fidbycount',
      points: clientData?.points || 0,
      firstRewardPoints: meta.points1 || 0,
      lastActivityMs,
    };
    commitCards();
  }, [commitCards]);

  const loadCardCache = useCallback((uid) => {
    if (!uid) return [];
    try {
      const raw = localStorage.getItem(`${CARD_CACHE_PREFIX}${uid}`);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return [];
      if (parsed.ts && Date.now() - parsed.ts > CARD_CACHE_TTL_MS) return [];
      return Array.isArray(parsed.data) ? parsed.data : [];
    } catch {
      return [];
    }
  }, []);

  const clearCardCache = useCallback((uid) => {
    if (!uid) return;
    localStorage.removeItem(`${CARD_CACHE_PREFIX}${uid}`);
  }, []);

  const attachMerchantListeners = useCallback(async (merchantId, uid, options = {}) => {
    const { docRef = null, initialData = null } = options;
    if (!merchantId || !uid) return;

    const alreadyAttached = attachedMerchants.current.has(merchantId);
    if (alreadyAttached && !docRef) {
      if (initialData) {
        await upsertCard(merchantId, initialData);
      }
      return;
    }

    attachedMerchants.current.add(merchantId);

    const teardownNew = () => {
      if (perMerchantNewUnsub.current[merchantId]) {
        perMerchantNewUnsub.current[merchantId]();
        delete perMerchantNewUnsub.current[merchantId];
      }
    };

    const teardownLegacy = () => {
      if (perMerchantLegacyUnsub.current[merchantId]) {
        perMerchantLegacyUnsub.current[merchantId]();
        delete perMerchantLegacyUnsub.current[merchantId];
      }
    };

    const dropCard = () => {
      delete dataMap.current[merchantId];
      attachedMerchants.current.delete(merchantId);
      commitCards();
    };

    const startLegacy = () => {
      const legacyRef = doc(firestore, 'fidelity', merchantId, 'list', uid);
      let sawInitial = false;
      perMerchantLegacyUnsub.current[merchantId] = onSnapshot(
        legacyRef,
        async (snap) => {
          sawInitial = true;
          if (snap.exists()) {
            await upsertCard(merchantId, snap.data() || {});
          } else {
            teardownLegacy();
            dropCard();
          }
        },
        (err) => {
          console.error('legacy listener error:', err);
          teardownLegacy();
          if (!sawInitial) dropCard();
        }
      );
    };

    if (docRef) {
      teardownNew();
      if (initialData) await upsertCard(merchantId, initialData);
      perMerchantNewUnsub.current[merchantId] = onSnapshot(
        docRef,
        async (snap) => {
          if (snap.exists()) {
            await upsertCard(merchantId, snap.data() || {});
            commitCards();
          } else {
            teardownNew();
            startLegacy();
          }
        },
        (err) => {
          console.error('new-schema doc listener error:', err);
          teardownNew();
          startLegacy();
        }
      );
      return;
    }

    const listCol = collection(firestore, 'fidelity', merchantId, 'list');
    const qNew = query(listCol, where('clientUid', '==', uid), fsLimit(1));

    perMerchantNewUnsub.current[merchantId] = onSnapshot(
      qNew,
      async (snap) => {
        if (!snap.empty) {
          await upsertCard(merchantId, snap.docs[0].data() || {});
          commitCards();
        } else {
          teardownNew();
          startLegacy();
        }
      },
      (err) => {
        console.error('new-schema listener error:', err);
        teardownNew();
        startLegacy();
      }
    );
  }, [commitCards, upsertCard]);

  /* ============================
     Mount: use cache first, then bounded scan if needed
  ============================ */
  useEffect(() => {
    if (!authLoaded) return;
    const auth = getAuth();
    let cancelled = false;

    const unsub = onAuthStateChanged(auth, async (user) => {
      Object.values(perMerchantNewUnsub.current).forEach((fn) => fn && fn());
      perMerchantNewUnsub.current = {};
      Object.values(perMerchantLegacyUnsub.current).forEach((fn) => fn && fn());
      perMerchantLegacyUnsub.current = {};
      attachedMerchants.current = new Set();
      dataMap.current = {};

      if (!user) {
        if (currentUidRef.current) clearCardCache(currentUidRef.current);
        currentUidRef.current = null;
        setFidelityData([]);
        return;
      }

      const uid = user.uid;
      currentUidRef.current = uid;

      const cachedCards = loadCardCache(uid);
      if (cachedCards.length) {
        const hydrated = {};
        cachedCards.forEach((card) => {
          if (card && typeof card === 'object' && card.merchantId) {
            hydrated[card.merchantId] = card;
          }
        });
        dataMap.current = hydrated;
        commitCards();
      } else {
        setFidelityData([]);
      }

      const cacheKey = `fid_merchants_${uid}`;
      let cachedIds = [];
      try {
        const raw = localStorage.getItem(cacheKey);
        cachedIds = raw ? JSON.parse(raw) : [];
      } catch {}

      const unionIds = uniqSorted(Array.isArray(cachedIds) ? cachedIds : []);

      const attachFromList = async (ids) => {
        if (!Array.isArray(ids) || !ids.length) return [];
        const unique = uniqSorted(ids);
        const tasks = unique.map((mid) => attachMerchantListeners(mid, uid));
        const results = await Promise.allSettled(tasks);
        const ok = [];
        results.forEach((res, idx) => { if (res.status === 'fulfilled') ok.push(unique[idx]); });
        return ok;
      };

      await attachFromList(unionIds);

      if (attachedMerchants.current.size < 1) {
        try {
          const cgSnap = await getDocs(
            query(
              collectionGroup(firestore, 'list'),
              where('clientUid', '==', uid),
              fsLimit(LEGACY_SCAN_LIMIT)
            )
          );
          const foundNew = [];
          for (const docSnap of cgSnap.docs) {
            const merchantId = docSnap.ref?.parent?.parent?.id;
            if (!merchantId || attachedMerchants.current.has(merchantId)) continue;

            await attachMerchantListeners(merchantId, uid, {
              docRef: docSnap.ref,
              initialData: docSnap.data() || {},
            });
            foundNew.push(merchantId);
          }

          if (!foundNew.length) {
            const merchantsSnap = await getDocs(query(collection(firestore, 'fidelity'), fsLimit(LEGACY_SCAN_LIMIT)));
            const found = [];
            for (const m of merchantsSnap.docs) {
              const merchantId = m.id;
              if (attachedMerchants.current.has(merchantId)) continue;

              const listRef = collection(firestore, 'fidelity', merchantId, 'list');
              const newQ = query(listRef, where('clientUid', '==', uid), fsLimit(1));
              const testNew = await getDocs(newQ);
              if (!testNew.empty) {
                await attachMerchantListeners(merchantId, uid);
                found.push(merchantId);
                continue;
              }

              const legacyRef = doc(firestore, 'fidelity', merchantId, 'list', uid);
              const legacySnap = await getDoc(legacyRef);
              if (legacySnap.exists()) {
                await attachMerchantListeners(merchantId, uid);
                found.push(merchantId);
              }
            }
            if (!cancelled && found.length) {
              const unique = uniqSorted([...(cachedIds || []), ...found]);
              localStorage.setItem(cacheKey, JSON.stringify(unique));
            }
          } else if (!cancelled && foundNew.length) {
            const unique = uniqSorted([...(cachedIds || []), ...foundNew]);
            localStorage.setItem(cacheKey, JSON.stringify(unique));
          }
        } catch (e) {
          console.error('bounded scan error:', e);
        }
      }
    });

    return () => {
      cancelled = true;
      Object.values(perMerchantNewUnsub.current).forEach((fn) => fn && fn());
      perMerchantNewUnsub.current = {};
      Object.values(perMerchantLegacyUnsub.current).forEach((fn) => fn && fn());
      perMerchantLegacyUnsub.current = {};
      unsub();
    };
  }, [attachMerchantListeners, authLoaded, clearCardCache, loadCardCache, commitCards]);

  /* ============================
     UI handlers
  ============================ */
  const handleBackClick = () => {
    if (getAuth().currentUser) navigate('/clientdocs');
  };
  const handleFidelityClick = (id) => navigate(`/clientfidviewer/${id}`);
  const goToTransfer = () => navigate('/transfertfid');

  /* ============================
     Render — même logique (2 colonnes), cap 4 colonnes max
  ============================ */
  return (
    <div className={`dashboard-container cf-page${canHover ? ' cf-can-hover' : ''}`}>
      <ClientNavbar />
      <style>{CF_STYLES}</style>

      {/* Modal création compte */}
      {showModal && (
        <div className="cf-overlay">
          <div className="cf-modal eb-fade-up" role="dialog" aria-modal="true">
            <button
              type="button"
              onClick={handleCloseModal}
              className="cf-modal__close"
              aria-label="Fermer"
              title="Fermer"
            >
              &times;
            </button>
            <h2 className="cf-modal__title">Créer mon compte</h2>
            <input type="email" className="eb-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Email" aria-label="Email" inputMode="email" autoComplete="email" />
            <div className="cf-pwd">
              <PasswordField
                value={newPassword}
                onChange={handlePwdChange}
                placeholder="Mot de passe"
                className="eb-input"
                inputStyle={newPassword && !isPwdValid ? { borderColor: 'var(--eb-danger)' } : undefined}
                autoComplete="new-password"
                pattern={PASSWORD_REGEX.source}
                title={PASSWORD_POLICY.message}
                aria-label="Mot de passe"
                aria-invalid={!!newPassword && !isPwdValid}
              />
            </div>
            <input type="text" className="eb-input" value={newPseudo} onChange={(e) => setNewPseudo(e.target.value)} placeholder="Pseudo" aria-label="Pseudo" autoComplete="nickname" />
            <LegalAccordion docId="cgu" checked={acceptedCGU} onChange={setAcceptedCGU} />
            <button
              type="button"
              onClick={handleCreateAccount}
              disabled={isCreateDisabled}
              className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
            >
              {creating ? <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" /> : null}
              {creating ? 'Création...' : 'Créer'}
            </button>
          </div>
        </div>
      )}

      <div className="cf-inner">
        {/* En-tête */}
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title cf-title">Fidélité</h1>
          </div>
          <div className="cf-actions">
            <button type="button" onClick={handleBackClick} className="eb-btn eb-btn--secondary">
              <FaArrowLeft aria-hidden="true" /> Retour
            </button>
            <button type="button" onClick={goToTransfer} className="eb-btn eb-btn--primary">
              Partager mes points
            </button>
          </div>
        </header>

        {/* Cartes fidélité — 2 colonnes sur mobile, jusqu'à 4 sur desktop */}
        {fidelityData.length > 0 ? (
          <div className="cf-grid">
            {fidelityData.map((item) => {
              const eligibleCount = item.Type === 'Fidbycount' && item.multiple - item.compteur === 1;
              const eligiblePoints = item.Type === 'Fidbypoints' && item.points >= item.firstRewardPoints;

              return (
                <div
                  key={item.merchantId}
                  onClick={() => handleFidelityClick(item.merchantId)}
                  className="cf-card"
                  role="button"
                  tabIndex={0}
                >
                  {/* header visuel */}
                  <div className="fid-card-header">
                    <div className="cf-card__avatar" data-initial={(item.merchantPseudo || '?').trim().charAt(0)}>
                      <MerchantLogo merchantId={item.merchantId} />
                    </div>
                    <h2 className="fid-card-title" title={item.merchantPseudo}>
                      {item.merchantPseudo}
                    </h2>
                  </div>

                  {item.Type === 'Fidbycount' ? (
                    <>
                      <p className="cf-card__count">
                        {item.compteur} / {item.multiple} passages
                      </p>

                      {item.gift && (
                        <div className={`cf-reward${eligibleCount ? ' is-eligible' : ''}`}>
                          <p className="cf-reward__label">
                            {eligibleCount ? 'Gain au prochain passage :' : 'Gain verrouillé :'}
                          </p>
                          <p className="cf-reward__gift">{item.gift}</p>
                          {item.code && (
                            <p className="cf-reward__code">code : {item.code}</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={`cf-reward${eligiblePoints ? ' is-eligible' : ''}`}>
                      <p className="cf-reward__gift" style={{ fontWeight: 700 }}>
                        Vos points : {item.points} pts
                      </p>
                      <p className="cf-reward__label" style={{ marginTop: 4, marginBottom: 0 }}>
                        {eligiblePoints ? 'Cliquez pour voir les récompenses' : `À ${item.firstRewardPoints} pts pour récompense`}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="cf-empty">Aucune donnée de fidélité à afficher</div>
        )}
      </div>
    </div>
  );
};

const CF_STYLES = `
.dashboard-container.cf-page { align-items: stretch; }
.cf-inner { width: 100%; max-width: var(--eb-content-max); margin: 0 auto; }
.dashboard-container .cf-title { text-align: left; margin: 0; font-size: 26px; }
.cf-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-left: auto; }

.cf-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 16px;
  align-items: stretch;
}
.cf-card {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;
}
.cf-can-hover .cf-card:hover { border-color: var(--eb-brand-300); box-shadow: var(--eb-shadow-md); transform: translateY(-2px); }
.cf-card:focus-visible { outline: none; box-shadow: var(--eb-ring); }

.fid-card-header {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--eb-border);
}
.cf-card__avatar {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--eb-brand-50);
  border: 1px solid var(--eb-border);
  color: var(--eb-brand);
  font-weight: 700;
  font-size: 22px;
  text-transform: uppercase;
}
.cf-card__avatar:empty::after { content: attr(data-initial); }
.cf-card__avatar .fid-card-logo {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  box-shadow: none !important;
}
.dashboard-container h2.fid-card-title {
  max-width: 100%;
  margin: 0;
  font-size: 15.5px;
  font-weight: 700;
  color: var(--eb-text);
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cf-card__count { margin: 0; font-size: 14px; font-weight: 500; color: var(--eb-text-2); font-variant-numeric: tabular-nums; }

.cf-reward {
  width: 100%;
  margin-top: auto;
  padding: 12px;
  border-radius: var(--eb-radius);
  background: var(--eb-surface-2);
  border: 1px solid var(--eb-border);
}
.cf-reward.is-eligible { background: var(--eb-warning-bg); border-color: #fde3b0; }
.cf-reward__label { margin: 0; font-size: 12.5px; font-weight: 600; color: var(--eb-muted); }
.cf-reward.is-eligible .cf-reward__label { color: var(--eb-warning); }
.cf-reward__gift { margin: 4px 0 0; font-size: 14px; color: var(--eb-text); overflow-wrap: anywhere; }
.cf-reward__code { margin: 6px 0 0; font-size: 12.5px; color: var(--eb-muted); font-family: var(--eb-font-mono); }

.cf-empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--eb-muted);
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
}

/* Modale */
.cf-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 35, 56, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
.cf-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  background: var(--eb-surface);
  border-radius: var(--eb-radius-xl);
  box-shadow: var(--eb-shadow-lg);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}
.dashboard-container .cf-modal__title { margin: 0 0 4px; padding-right: 44px; text-align: left; font-size: 22px; font-weight: 700; }
.cf-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: var(--eb-radius-sm);
  background: transparent;
  color: var(--eb-muted);
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}
.cf-modal__close:hover { background: var(--eb-surface-2); color: var(--eb-text); }
.cf-pwd > div { margin-bottom: 0 !important; }
.cf-pwd > ul { margin: 10px 0 0 !important; }
.cf-pwd .eb-input { padding-right: 44px; }

@media (max-width: 600px) {
  .cf-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
  .cf-card { padding: 16px 12px; gap: 10px; }
  .cf-card__avatar { width: 56px; height: 56px; }
  .dashboard-container .cf-title { font-size: 22px; }
  .cf-actions { width: 100%; }
  .cf-actions .eb-btn { flex: 1 1 0; padding: 0 12px; }
}
@media (max-width: 480px) {
  .cf-overlay { align-items: flex-end; padding: 0; }
  .cf-modal {
    max-width: none;
    max-height: calc(100dvh - 24px);
    border-radius: var(--eb-radius-xl) var(--eb-radius-xl) 0 0;
    padding: 22px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  }
}
@media (max-width: 350px) {
  .cf-grid { grid-template-columns: 1fr; }
}
`;

export default ClientFid;
